import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { activitiesApi } from "@store/api/activities";
import { meetingApi } from "@store/api/meeting";
import { agendaApi } from "@store/api/agenda";
import { activityTypes } from "src/constants/activities";
import {
  getMeetingLiveActivityFromPayload,
  getMeetingLiveToolsFromPayload,
  getMeetingObjectFromPayload,
} from "@store/utils/meetingPayload";

const initialState = {
  activities: [],
  liveActivity: {},
  liveTools: [],
  selectedActivity: {},
  options: [],
  response: [],
  userResponse: [],
  cachedType: "",
  cachedTitle: "",
  showResult: false,
  isLoading: true,
  gracePeriodActivities: [],
};

export const activitiesSlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    reorderActivity: (state, action) => {
      const { source, destination } = action.payload;

      const reorderedActivities = Array.from(state.activities);
      const [removed] = reorderedActivities.splice(source.index, 1);
      reorderedActivities.splice(destination.index, 0, removed);

      state.activities = reorderedActivities;
    },
    updateSelectedActivity: (state, action) => {
      const { activity } = action.payload;
      state.selectedActivity = activity;
    },
    updateLiveActivityTool: (state, action) => {
      const { activity } = action.payload;
      state.liveTools = state.liveTools.concat(activity);
    },
    updateLiveActivity: (state, action) => {
      const { activity } = action.payload;
      state.liveActivity = activity || {};
      state.showResult = activity.settings.showResult;
    },
    addActivityResponse: (state, { payload }) => {
      const { activity } = payload;
      activitiesSlice.caseReducers.updateActivityInList(state, activity);

      if (activity.type === activityTypes.share) {
        const toolIndex = state.liveTools.findIndex(
          (tool) => tool._id === activity._id
        );
        if (toolIndex !== -1) {
          state.liveTools[toolIndex] = activity;
        }
      }
    },
    updateParticipantMeetingEnd: (state, { payload }) => {
      const { activity } = payload;
      state.response = activity.response || [];
      state.options = activity.options;
      state.cachedType = activity.type;
      state.cachedTitle = activity.title;
      state.showResult = activity.settings.showResult;
      state.liveActivity = {};
    },
    addUserResponse: (state, { payload }) => {
      const { userResponse } = payload;
      state.userResponse = userResponse;
    },
    updateActivityInList: (state, payload) => {
      const index = state.activities.findIndex((el) => el._id === payload._id);
      if (index !== -1) {
        state.activities[index] = payload;
      }
    },
    endActivity: (state, { payload }) => {
      const activity = { ...payload };
      state.liveActivity = {};
      activitiesSlice.caseReducers.updateActivityInList(state, activity);
    },
    checkForLiveActivity: (state) => {
      const liveActivity = state.activities.filter(
        (activity) => activity.live && activity.type !== activityTypes.share
      );
      state.liveActivity = liveActivity[0] || {};
    },
    prepareStateForLiveActivity: (state) => {
      state.response = [];
      state.options = [];
      state.userResponse = [];
      state.showResult = false;
    },
    clearActivityState: () => initialState,
    addToGracePeriod: (state, action) => {
      const { activityId } = action.payload;
      if (!state.gracePeriodActivities.includes(activityId)) {
        state.gracePeriodActivities.push(activityId);
      }
    },
    removeFromGracePeriod: (state, action) => {
      const { activityId } = action.payload;
      state.gracePeriodActivities = state.gracePeriodActivities.filter(
        (id) => id !== activityId
      );
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      activitiesApi.endpoints.getActivities.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        state.activities = payload;
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.createActivity.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        state.activities = state.activities.concat([payload.activity]);
        state.selectedActivity = {};
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.updateActivity.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        state.selectedActivity = {};
        activitiesSlice.caseReducers.updateActivityInList(state, payload);
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.deleteActivity.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        const updatedActivity = state.activities.filter(
          (activity) => activity._id !== payload._id
        );
        state.activities = updatedActivity;
        state.selectedActivity = {};
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.goLive.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        if (payload.type === activityTypes.share) {
          state.liveTools = state.liveTools.concat(payload);
        } else {
          state.liveActivity = payload;
        }
        activitiesSlice.caseReducers.updateActivityInList(state, payload);
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.stopLive.matchFulfilled,
      (state, { payload }) => {
        state.isLoading = false;
        state.liveActivity = {};
        activitiesSlice.caseReducers.updateActivityInList(state, payload);
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.viewActivity.matchFulfilled,
      (state, { payload }) => {
        const { activity } = payload;
        state.isLoading = false;
        if (!activity) return;

        const toolIndex = state.liveTools.findIndex(
          (tool) => tool._id === activity._id
        );
        if (toolIndex !== -1) {
          state.liveTools[toolIndex] = activity;
        }
      }
    );
    builder.addMatcher(
      activitiesApi.endpoints.respondToActivity.matchFulfilled,
      (state, { payload }) => {
        const { activity } = payload;
        state.isLoading = false;
        state.cachedType = activity.type;
        state.liveActivity.response = activity.response || [];
        state.response = activity.response;
      }
    );
    builder.addMatcher(
      ({ type }) =>
        type.startsWith(activitiesApi.reducerPath) && type.endsWith("/pending"),
      (state) => {
        state.isLoading = true;
      }
    );
    builder.addMatcher(
      ({ type }) =>
        type.startsWith(activitiesApi.reducerPath) &&
        type.endsWith("/rejected"),
      (state) => {
        state.isLoading = false;
      }
    );
    builder.addMatcher(
      isAnyOf(
        meetingApi.endpoints.checkExistingMeeting.matchFulfilled,
        meetingApi.endpoints.joinMeetingFromWeb.matchFulfilled,
        meetingApi.endpoints.checkWebUserLiveMeeting.matchFulfilled,
        meetingApi.endpoints.joinWebMeeting.matchFulfilled,
        meetingApi.endpoints.getActiveWebMeeting.matchFulfilled
      ),
      (state, { payload }) => {
        if (!getMeetingObjectFromPayload(payload)) return;
        state.liveActivity = getMeetingLiveActivityFromPayload(payload);
        state.liveTools = getMeetingLiveToolsFromPayload(payload);
      }
    );
    builder.addMatcher(
      agendaApi.endpoints.stopLiveAgenda.matchFulfilled,
      (state, { payload }) => {
        state.liveActivity = {};
        state.liveTools = [];
        // Use activities from backend response instead of optimistic update
        if (payload.activities) {
          state.activities = payload.activities;
        }
      }
    );
  },
});

const { reducer, actions } = activitiesSlice;

export const {
  reorderActivity,
  updateLiveActivityTool,
  updateParticipantMeetingEnd,
  showResult,
  addUserResponse,
  addActivityResponse,
  updateLiveActivity,
  checkForLiveActivity,
  endActivity,
  updateSelectedActivity,
  prepareStateForLiveActivity,
  clearActivityState,
  addToGracePeriod,
  removeFromGracePeriod,
} = actions;

export default reducer;
